import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_JA } from 'material-react-table/locales/ja';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { Box, Button, Typography, lighten } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';

// self defined stuff
import { IssueStatusKenshin } from 'shared/constants/kenshin/issues';
import { filterData, generateRowSelectionObject } from './utils';
import { getUserFullName } from 'utils/name';
import localData from 'utils/localData';
import { getKenshinTableColumns } from './tableColumns';
import logger from 'utils/logger';
import { getTablesTheme } from 'components/CustomThemes';
import { LANGUAGE, USER_DATA } from 'shared/constants/users';

const propTypes = {
  issue: PropTypes.any,
  doctors: PropTypes.any,
  setBatchEditModalIsOpen: PropTypes.func,
  setSelectedCases: PropTypes.func,
  isClient: PropTypes.bool,
  setViewModalOpen: PropTypes.func.isRequired,
  setViewportWebUrls: PropTypes.func.isRequired,
};

const log = logger.createLogger('KenshinTable');

const Table = ({
  issue,
  doctors,
  setBatchEditModalIsOpen,
  setSelectedCases,
  isClient,
  setViewModalOpen,
  setViewportWebUrls,
}) => {
  // diagnosis and doctor info should be hidden from sender organization;
  const isHiding =
    isClient &&
    [
      IssueStatusKenshin.DOCTORASSIGNED,
      IssueStatusKenshin.REPORTREADY,
    ].includes(issue.progress);

  const diagnosticResultFilterSelectOptions = [
    {
      label: intl.get('kenshin_case_diagnostic_result_to_be_diagnosed'),
      value: 'TO_BE_DIAGNOSED',
    },
    {
      label: intl.get('kenshin_case_diagnostic_result_diagnosed_healthy'),
      value: 'DIAGNOSED_HEALTHY',
    },
    {
      label: intl.get('kenshin_case_diagnostic_result_diagnosed_sick'),
      value: 'DIAGNOSED_SICK',
    },
    {
      label: intl.get('kenshin_case_diagnostic_result_diagnosed_unknown'),
      value: 'DIAGNOSED_UNKNOWN',
    },
  ];

  const CONSTANT_NO_DOCTOR_ASSIGNED = intl.get(
    'kenshin_issue_details_case_status_no_doctor_assigned'
  );

  const removeDuplicatesById = (arr) => {
    const seenIds = new Set();
    return arr.filter((obj) => {
      if (!seenIds.has(obj.doctorId)) {
        seenIds.add(obj.doctorId);
        return true;
      }
      return false;
    });
  };

  let doctorsFilterOptions;
  if (isClient) {
    const tmpArr = issue.cases.map((item) => ({
      doctorId: item.doctorId,
      doctorFullName: getUserFullName(
        item.doctorFirstName,
        item.doctorLastName
      ),
    }));

    var uniqueDoctorsById = removeDuplicatesById(tmpArr);
    uniqueDoctorsById = uniqueDoctorsById.filter(
      (item) => item.doctorId && item.doctorFullName
    );

    doctorsFilterOptions = uniqueDoctorsById.map((doctor) => ({
      label: doctor.doctorFullName,
      value: doctor.doctorId,
    }));
  } else {
    doctorsFilterOptions = doctors.map((doctor) => ({
      label: getUserFullName(doctor.firstName, doctor.lastName),
      value: doctor.id,
    }));
  }

  doctorsFilterOptions.push({
    label: CONSTANT_NO_DOCTOR_ASSIGNED,
    value: -1,
  });

  const columns = useMemo(
    () =>
      getKenshinTableColumns(
        isHiding,
        doctorsFilterOptions,
        diagnosticResultFilterSelectOptions,
        setViewportWebUrls,
        setViewModalOpen
      ),
    [
      isHiding,
      doctorsFilterOptions,
      diagnosticResultFilterSelectOptions,
      setViewportWebUrls,
      setViewModalOpen,
    ]
  );

  const allData = issue.cases.map((item, index) => ({
    index: index,
    displayIndex: index + 1,
    ...item,
    doctorId: item.doctorId == null ? -1 : item.doctorId,
  }));

  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [data, setData] = useState(allData); // This is the data shown in the table; after going through filters;

  useEffect(() => {
    const filteredData = filterData(allData, columnFilters);
    // Convert the indexes in filteredData to a Set for efficient lookup
    const dataIndexes = new Set(filteredData.map((item) => item.index));
    // Iterate over the keys of rowSelection
    Object.keys(rowSelection).forEach((key) => {
      const index = parseInt(key, 10); // Must convert key to number
      if (!dataIndexes.has(index)) delete rowSelection[index];
    });
    setRowSelection({ ...rowSelection });

    setData([...filteredData]);
  }, [columnFilters, issue]);

  const preferredLanguage = localData.get(USER_DATA.PREFFERED_LANGUAGE);
  let MRT_Localization = null;
  if (preferredLanguage === LANGUAGE.JAPANSESE) {
    MRT_Localization = MRT_Localization_JA;
  } else {
    MRT_Localization = MRT_Localization_EN;
  }

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    localization: {
      ...MRT_Localization,
      toggleSelectAll: intl.get('share_mrt2_text_label_body_toggle_select_all'),
      noRecordsToDisplay: intl.get('share_muitable_text_label_body_no_match'),
    },

    enableColumnFilterModes: false,
    enableStickyHeader: true,
    enableRowSelection: true,
    getRowId: (row) => row.index,
    enableSelectAll: true,
    selectAllMode: 'page', // 'all'
    showColumnFilters: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      density: 'compact',
      showColumnFilters: true,
    },
    onColumnFiltersChange: setColumnFilters, //hoist internal columnFilters state to your state
    autoResetPageIndex: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',

    // UI styles config
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    muiTableContainerProps: { sx: { maxHeight: '60vh' } }, // set the max height of the table to be 60% of the viewport height;
    muiTablePaperProps: { style: { zIndex: '1' } },
    muiTableHeadCellProps: {
      sx: {
        fontSize: '15px',
      },
    },

    muiTableBodyRowProps: {
      sx: {
        border: 1,
      },
    },

    muiTableBodyCellProps: {
      size: 'small',
      sx: {
        fontSize: '14px',
        '&:hover': {
          fontWeight: 'bold',
        },
      },
    },
    muiFilterTextFieldProps: {
      sx: { width: '100%', p: '1px 0 1px' },
    },
    displayColumnDefOptions: {
      'mrt-row-select': {
        size: 10, //adjust the size of the row select column
        grow: false, //new in v2.8 (default is false for this column)
      },
    },

    renderTopToolbar: ({ table }) => {
      const handleSelectAllButtonClick = () => {
        if (Object.keys(rowSelection).length > 0) {
          table.getState().rowSelection = {};
          setRowSelection({});
          // setSelectingSome(false);
        } else {
          const rowSelection = generateRowSelectionObject(data);
          table.getState().rowSelection = rowSelection;
          setRowSelection(rowSelection);
          // setSelectingSome(true);
        }
      };

      const handleBatchEditClick = () => {
        const selectedIndexes = Object.keys(rowSelection).map((key) =>
          parseInt(key)
        );
        const selectedCases = selectedIndexes
          .map((index) => allData[index])
          .filter(Boolean);
        setSelectedCases(selectedCases);
        setBatchEditModalIsOpen(true);
      };

      //! TODO CSV is only in english
      const handleExportCsv = () => {
        const csvContent =
          'data:text/csv;charset=utf-8,' +
          '\uFEFF' +
          'CaseID, Modality, Bodypart, Diagnosis, DiagnosticNote\n' +
          issue.cases
            .map((case_) => {
              const caseId = case_.id;
              const modality = case_.modality;
              const bodypart = case_.bodypart;
              const diagnosis = case_.diagnosis;
              const diagnosticNote = case_.diagnosticNote;
              return (
                caseId +
                ',' +
                modality +
                ',' +
                bodypart +
                ',' +
                diagnosis +
                ',' +
                diagnosticNote
              );
            })
            .join('\n');

        const encodeUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodeUri);
        link.setAttribute('download', `all-diagnosis.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}>
          <Box
            sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}></Box>
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Typography>
              {intl
                .get('kenshin_kanban_case_table_text_already_selected')
                .replace('{arg1}', `${data.length}`)}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                color="info"
                disabled={data.length === 0}
                onClick={handleSelectAllButtonClick}
                variant="contained">
                {Object.keys(rowSelection).length > 0
                  ? intl.get(
                      'kenshin_kanban_case_batch_edit_modal_button_clear_selected'
                    )
                  : intl.get(
                      'kenshin_kanban_case_batch_edit_modal_button_select_all'
                    )}
              </Button>
              {isClient === false && (
                <Button
                  color="info"
                  disabled={Object.keys(rowSelection).length === 0}
                  onClick={() => {
                    handleBatchEditClick();
                    table.resetRowSelection();
                  }}
                  variant="contained"
                  startIcon={<EditIcon />}>
                  {intl.get('kenshin_issue_details_case_table_button_assign')}
                </Button>
              )}
              <Button
                color="info"
                onClick={handleExportCsv}
                variant="contained"
                startIcon={<DownloadIcon />}>
                {intl.get('kenshin_issue_details_case_table_button_csv')}
              </Button>
            </Box>
          </Box>
        </Box>
      );
    },
  });

  return (
    //Setting Material UI locale as best practice to result in better accessibility
    <ThemeProvider theme={getTablesTheme(false)}>
      <MaterialReactTable
        id="KanbanKenshinIssueDetailsCasesTable"
        table={table}
      />
    </ThemeProvider>
  );
};

Table.propTypes = propTypes;

export default Table;
